import { Link } from "react-router-dom";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { StatsGrid, type StatTile } from "@/components/ui/stats-grid";
import { useAdminConsole } from "@/hooks/queries/useAdminConsole";
import type { AdminConsoleData } from "@/hooks/queries/admin-console-types";
import { toAdminConsoleError } from "@/routes/admin/admin-console-error";
import {
  timelineSentence,
  timelineTimestamp,
} from "@/routes/admin/admin-console-derivations";

// Screen 10 — the super_admin operational console (Story 15.7, FR58 / UX §13.5).
//
// SIX TILES IN A 2x3 GRID. Both halves are client rulings of 2026-08-30: the
// sixth tile stays, and it is laid out as two rows of three rather than six
// across. Six long labels in one `md` row give each about 118px at 768px, and
// "Live scoring enabled" does not fit that.
//
// The mockup itself lays five tiles out as 4 + 1, not "five in one row" — an
// earlier version of this comment said the latter and was wrong about a
// client-validated artifact. `.stats` is `repeat(4, minmax(0, 1fr))`
// (`amps-hifi-screens.css:282-286`) with five `.stat` children on Screen 10, so
// the fifth already wraps. That makes 2x3 a smaller departure than it reads as:
// the mockup was never a single row, and the ruling chose an even split over an
// uneven one.
//
// The sixth — "Orgs at seat limit" — is AC7b,
// the console half of the PM ruling of 2026-08-27: the directory shows both the
// contract axis and the seat axis per row, so the console must show both in
// aggregate. Without it the seat axis is invisible here exactly as it was masked
// there. Recorded as departure U3.
//
// TWO TILE LABELS DIVERGE FROM THE MOCKUP, both deliberately:
//   • "Active clinicians" → "Registered clinicians" (D1). The mockup put
//     "Active clinicians" beside "Active subscriptions", and the client's rename
//     of the second to "Active clinicians" would have left two tiles with one
//     name counting different things. The older tile counts ACCOUNTS, and its
//     label now says so.
//
//     The rename arrived title-cased and shipped that way, alone among six
//     sentence-case labels. The client settled it 2026-08-30: sentence case, in
//     line with the other five. On an operations screen the odd one out reads as
//     a different product rather than as emphasis.
//   • "Renewals in 30 days" → the window the SERVER counted (U1). The number is
//     read off `renewal_window_days` rather than written into this copy, so the
//     label and the count cannot disagree — which is the failure the mockup's
//     literal 30 over a 90-day count would have been. FR49's 30-day reminder
//     EMAIL is a different surface and is untouched.
//
// NO USER-OVERSIGHT TABLE (D2). UX §13.5 gives Screen 10 a six-column user table
// that Screen 20 already is (Story 11.1). A second copy is double maintenance
// and guaranteed drift, so this screen LINKS there instead.
//
// NO FILTERS ANYWHERE — the mockup has none, and none are added.

const HEADING_ID = "admin-console-heading";
const TIMELINE_HEADING_ID = "admin-console-timeline-heading";

/**
 * The six KPI tiles, in the mockup's order with AC7b's appended.
 *
 * The renewals label is BUILT from the window the response carried. A literal
 * here would be a second definition of the window, and the tile beneath it would
 * be free to disagree with the count above it.
 */
export function consoleStats(data: AdminConsoleData): StatTile[] {
  return [
    { label: "Registered clinicians", value: data.kpis.registered_clinicians },
    { label: "Active clinicians", value: data.kpis.active_clinicians },
    {
      label: `Renewals in ${data.renewal_window_days} days`,
      value: data.kpis.renewals_in_window,
    },
    { label: "Blocked users", value: data.kpis.blocked_users },
    { label: "Live scoring enabled", value: data.kpis.live_scoring_enabled },
    { label: "Orgs at seat limit", value: data.kpis.orgs_at_seat_limit },
  ];
}

export function AdminConsoleScreen() {
  const { data, isPending, error } = useAdminConsole();
  const banner = error ? toAdminConsoleError(error) : null;

  return (
    <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 id={HEADING_ID} className="display-xs text-[color:var(--text-primary)]">
          Operational oversight
        </h1>
        <p className="text-sm text-[color:var(--text-secondary)]">
          Review subscription, calibration, and access state from one operational
          console without exposing unrestricted clinical records.
        </p>
      </div>

      {banner ? (
        <Banner variant={banner.variant} title={banner.title}>
          <p>{banner.body}</p>
          {banner.code ? (
            <p className="text-sm">{`Reference code: ${banner.code}`}</p>
          ) : null}
        </Banner>
      ) : null}

      {/* Loading renders NOTHING — there is no skeleton primitive in
          components/ui, and every sibling admin screen takes the same posture. */}
      {!isPending && !error && data ? (
        <>
          <StatsGrid stats={consoleStats(data)} columns={3} />

          {/* D2 — the mockup's user-oversight table, as the link it should have
              been: Screen 20 IS that table, filtered, sorted and paginated. */}
          <div className="flex flex-col gap-3 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-sm font-semibold text-[color:var(--text-primary)]">
                User oversight
              </h2>
              <p className="text-sm text-[color:var(--text-tertiary)]">
                Review account, subscription, calibration, and access state per
                user in the user directory.
              </p>
            </div>
            <div>
              <Button asChild variant="secondary" size="sm">
                <Link to="/admin/users">Open user directory</Link>
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
            <h2
              id={TIMELINE_HEADING_ID}
              className="text-sm font-semibold text-[color:var(--text-primary)]"
            >
              Audit-relevant activity
            </h2>

            {data.activity_timeline.length === 0 ? (
              <Banner variant="info" title="No audit-relevant activity yet">
                <p>
                  Organization, certificate and subscription events appear here as
                  they are recorded.
                </p>
              </Banner>
            ) : (
              <ol
                aria-labelledby={TIMELINE_HEADING_ID}
                className="flex flex-col gap-3"
              >
                {data.activity_timeline.map((entry) => (
                  <li key={entry.event_id} className="flex flex-col gap-1">
                    <strong className="text-sm font-semibold text-[color:var(--text-primary)] [font-variant-numeric:tabular-nums]">
                      {timelineTimestamp(entry.occurred_at)}
                    </strong>
                    <span className="text-sm text-[color:var(--text-tertiary)]">
                      {timelineSentence(entry)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}

export default AdminConsoleScreen;
